import { z } from "zod";

const AUTH_SESSION_KEY = "auth-session";
// pre-refactor location of the session fields inside the app-store blob
const LEGACY_STORAGE_KEY = "app-store";

const authSessionSchema = z.object({
  tokenExpiresAt: z.string().nullable().catch(null),
  authMethod: z.enum(["jwt", "workos"]).nullable().catch(null),
});

export type AuthSession = z.infer<typeof authSessionSchema>;

const emptySession: AuthSession = { tokenExpiresAt: null, authMethod: null };

function parseSession(raw: string | null): AuthSession | null {
  if (!raw) return null;
  try {
    return authSessionSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

// localStorage is shared across tabs, so a session written by another tab
// (e.g. after winning the refresh lock) is visible here immediately
export function readAuthSession(): AuthSession {
  try {
    const session = parseSession(localStorage.getItem(AUTH_SESSION_KEY));
    if (session) return session;

    // one-time migration from the app-store blob where these fields lived;
    // without it existing workos sessions would refresh via the jwt endpoint
    // TODO: delete in future
    const legacy = parseSession(localStorage.getItem(LEGACY_STORAGE_KEY));
    if (legacy && (legacy.tokenExpiresAt || legacy.authMethod)) {
      writeAuthSession(legacy);
      return legacy;
    }
  } catch {
    // storage unavailable: treat as no session; cookies stay authoritative
  }
  return emptySession;
}

export function writeAuthSession(session: AuthSession): void {
  try {
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  } catch {
    // storage unavailable: the 401 retry path still recovers
  }
}

export function setAuthMethod(authMethod: "jwt" | "workos"): void {
  writeAuthSession({ ...readAuthSession(), authMethod });
}

export function setTokenExpiry(expiresInSeconds: number): void {
  writeAuthSession({
    ...readAuthSession(),
    tokenExpiresAt: new Date(
      Date.now() + expiresInSeconds * 1000,
    ).toISOString(),
  });
}

export function clearAuthSession(): void {
  try {
    // authMethod is a routing hint rather than a credential, and it outlives the
    // session on purpose: dropping it sends the next refresh of a still-valid
    // workos session to the jwt endpoint, which answers REFRESH_TOKEN_MISSING
    writeAuthSession({
      ...emptySession,
      authMethod: readAuthSession().authMethod,
    });
    // also scrub the legacy blob so migration cannot resurrect the session
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const rest = parsed as Record<string, unknown>;
        delete rest.tokenExpiresAt;
        delete rest.authMethod;
        localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(rest));
      }
    }
  } catch {
    // storage unavailable: nothing to clear
  }
}
